import React, { useEffect, useState } from "react";
import {
  getAIModelSetupsList,
  deleteAIModelSetup,
} from "../../services/aiModelSetupsService";
import { getAIModelMastersList } from "../../services/aiModelMastersService";
import { paginateData, calculateTotalPages } from "../../assets/js/script";
import TableHeader from "../Common/TableComponent/TableHeader";
import TableToolbar from "../Common/TableComponent/TableToolbar";
import TablesRow from "../Common/TableComponent/TablesRow";
import { renderStatusBadge } from "../Common/TableComponent/tableBadges";
import { Pagination } from "../Common/TableComponent/Pagination";
import { AddAIModelSetup } from "./AddAIModelSetup";
import { Loading } from "../Common/OtherElements/Loading";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { confirmDelete } from "../Common/OtherElements/confirmDeleteClone";
import { TableDataStatusError } from "../Common/OtherElements/TableDataStatusError";
import { handleErrors } from "../../utils/errorHandler";
import { usePageLevelAccess } from "../../hooks/usePageLevelAccess";

const getSetupId = (item) => item.id ?? item.Id;

const getModelId = (item) => item.modelId ?? item.ModelId;

const getIdentityName = (item) =>
  item.identityName || item.IdentityName || "-";

const getClientId = (item) => item.clientId || item.ClientId || "-";

const getApiKey = (item) => item.apiKey || item.ApiKey || "";

const isDefaultSetup = (item) => {
  const value = item.isDefault ?? item.IsDefault;
  return value === true || value === "true" || value === 1;
};

const maskApiKey = (key) => {
  if (!key) return "-";
  if (key.length <= 4) return "********";
  return `********${key.slice(-4)}`;
};

const renderIsDefaultBadge = (item) => {
  const isDefault = isDefaultSetup(item);
  return (
    <span className={`badge badge-soft-${isDefault ? "success" : "secondary"}`}>
      {isDefault ? "Default" : "Non Default"}
    </span>
  );
};

const ApiKeyCell = ({ apiKey, rowId, revealedKeys, onToggle }) => {
  const isRevealed = revealedKeys[rowId];
  const displayValue = isRevealed ? apiKey || "-" : maskApiKey(apiKey);

  return (
    <span className="d-inline-flex align-items-center gap-2">
      <span>{displayValue}</span>
      {apiKey && (
        <span
          className="cursor-pointer"
          onClick={() => onToggle(rowId)}
          title={isRevealed ? "Hide API Key" : "Show API Key"}
        >
          <i
            className={
              isRevealed ? "mdi mdi-eye-off align-middle" : "mdi mdi-eye align-middle"
            }
          ></i>
        </span>
      )}
    </span>
  );
};

export const ManageAIModelSetupsContent = () => {
  const [pageAccessDetails, setPageAccessDetails] = useState({});
  const pageLevelAccessUrl = "ai-model-setups";
  const navigate = useNavigate();
  const { pageAccessData } = usePageLevelAccess(pageLevelAccessUrl);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [setupsList, setSetupsList] = useState([]);
  const [modelMastersList, setModelMastersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSetup, setSelectedSetup] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [revealedKeys, setRevealedKeys] = useState({});

  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.addAccess && !pageAccessData.viewAccess) {
        navigate("/404-error-page");
      } else {
        setPageAccessDetails(pageAccessData);
      }
    } else {
      console.log("No page access details found");
    }
  }, [pageAccessData, navigate]);

  const fetchModelMasters = async () => {
    try {
      const response = await getAIModelMastersList();
      const list = response.data?.result || response.data || [];
      setModelMastersList(Array.isArray(list) ? list : []);
    } catch (error) {
      handleErrors(error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getAIModelSetupsList();
      const list = response.data?.result || response.data || [];
      setSetupsList(Array.isArray(list) ? list : []);
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModelMasters();
    fetchData();
  }, []);

  const resolveModelName = (item) => {
    const embedded =
      item.modelName ||
      item.ModelName ||
      item.aiModelName ||
      item.AIModelName;
    if (embedded) return embedded;

    const modelId = getModelId(item);
    const master = modelMastersList.find(
      (m) => (m.id ?? m.Id) === modelId
    );
    if (master) {
      return master.modelName || master.ModelName || "-";
    }
    return modelId ? `Model #${modelId}` : "-";
  };

  const filteredData = setupsList.filter((item) => {
    const modelName = resolveModelName(item).toLowerCase();
    const identity = getIdentityName(item).toLowerCase();
    const clientId = getClientId(item).toLowerCase();
    const query = searchQuery.toLowerCase();
    return (
      modelName.includes(query) ||
      identity.includes(query) ||
      clientId.includes(query)
    );
  });

  const currentData = paginateData(filteredData, currentPage, entriesPerPage);
  const totalPages = calculateTotalPages(filteredData.length, entriesPerPage);

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const toggleApiKeyVisibility = (rowId) => {
    setRevealedKeys((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const handleDelete = async (setupId) => {
    const confirmed = await confirmDelete("AI Model Setup");
    if (confirmed) {
      try {
        await deleteAIModelSetup(setupId);
        setSetupsList((prev) =>
          prev.filter((item) => getSetupId(item) !== setupId)
        );
        Swal.fire(
          "Deleted!",
          "The AI model setup has been deleted successfully.",
          "success"
        );
        fetchData();
      } catch (error) {
        handleErrors(error);
      }
    }
  };

  return (
    <>
      {pageAccessDetails.addAccess ? (
        <AddAIModelSetup
          editMode={editMode}
          initialData={selectedSetup ?? {}}
          onSuccess={fetchData}
          setSelectedSetup={setSelectedSetup}
          setEditMode={setEditMode}
        />
      ) : (
        ""
      )}

      {pageAccessDetails.viewAccess ? (
        <div className="row">
          <div className="col-xxl-12">
            <div className="card mt-xxl-n5">
              <div className="card-header">
                <h5 className="mb-sm-2 mt-sm-2">Manage AI Model Setups</h5>
              </div>
              <div className="card-body manage-amenity-master-card-body">
                <TableToolbar
                  entriesPerPage={entriesPerPage}
                  onEntriesChange={handleEntriesChange}
                  searchValue={searchQuery}
                  searchPlaceholder="Search..."
                  onSearchChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />

                {loading ? (
                  <Loading />
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle table-bordered">
                      <TableHeader
                        columns={[
                          "Sl No",
                          "AI Model",
                          "Identity Name",
                          "Client ID",
                          "API Key",
                          "Is Default",
                          "Status",
                          "Action",
                        ]}
                      />
                      <tbody className="manage-page-group-table-values">
                        {currentData.length === 0 ? (
                          <TableDataStatusError colspan="8" />
                        ) : (
                          currentData.map((item, index) => {
                            const rowId = getSetupId(item);
                            return (
                              <TablesRow
                                key={rowId}
                                rowData={{
                                  slNo:
                                    (currentPage - 1) * entriesPerPage +
                                    index +
                                    1,
                                  aiModel: resolveModelName(item),
                                  identityName: getIdentityName(item),
                                  clientId: getClientId(item),
                                  apiKey: (
                                    <ApiKeyCell
                                      apiKey={getApiKey(item)}
                                      rowId={rowId}
                                      revealedKeys={revealedKeys}
                                      onToggle={toggleApiKeyVisibility}
                                    />
                                  ),
                                  isDefault: renderIsDefaultBadge(item),
                                  status: renderStatusBadge(item),
                                }}
                                columns={[
                                  "slNo",
                                  "aiModel",
                                  "identityName",
                                  "clientId",
                                  "apiKey",
                                  "isDefault",
                                  "status",
                                ]}
                                hideIcons={false}
                                onEdit={() => {
                                  setSelectedSetup(item);
                                  setEditMode(true);
                                  window.scrollTo({ top: 0, behavior: "smooth" });
                                }}
                                onDelete={() => handleDelete(rowId)}
                                pageLevelAccessData={pageAccessDetails}
                              />
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalEntries={filteredData.length}
                  entriesPerPage={entriesPerPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
    </>
  );
};
